import { isLangCode, type LangCode, type PlaybackMode } from "@1sky/shared";

export interface Prefs {
  name: string;
  lang: LangCode;
  voiceConsent: boolean;
  playback: PlaybackMode;
}

const KEY = "1sky.prefs";

/** Browser tags whose base code differs from ours (Norwegian Bokmål/Nynorsk, Filipino, legacy codes). */
const ALIASES: Record<string, LangCode> = { nb: "no", nn: "no", fil: "tl", iw: "he", in: "id" };

/** The user's native language as the browser reports it: first supported entry of their language list. */
export function guessLang(tags: readonly string[] = browserLanguages()): LangCode {
  for (const tag of tags) {
    const base = tag.toLowerCase().split(/[-_]/)[0] ?? "";
    const code = ALIASES[base] ?? base;
    if (isLangCode(code)) return code;
  }
  return "en";
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

/** Saved preferences; the language defaults to the browser's until the user picks one. */
export function loadPrefs(): Prefs {
  const fallback: Prefs = { name: "", lang: guessLang(), voiceConsent: true, playback: "translated" };
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<Prefs>) } : fallback;
  } catch {
    return fallback;
  }
}

export function savePrefs(p: Partial<Prefs>): Prefs {
  const next = { ...loadPrefs(), ...p };
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode: prefs live for this page only */
  }
  return next;
}
